import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { CheckCircleOutline, Close, Public, Unpublished } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Collection, CollectionStatus } from "../../../../models/collection";
import RichTextEditor from "../../components/rich-text/rich-text-editor";
import PublishCollectionDialog from "./publish-collection-dialog";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import { Hub } from "../../../../models/hub";
import { useSnackbar } from "../../../../components/snackbar";
import isHubActivated from "../../../../utils/is-hub-activated";

interface CollectionDetailsDrawerProps {
    classroom_id: string;
    collection: Collection | undefined;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    hub: Hub;
}

export default function CollectionDetailsDrawer({
    classroom_id,
    collection,
    open,
    setOpen,
    hub
}: CollectionDetailsDrawerProps) {
    const { updateCollection } = useClassroomActions();
    
    const [updateLoading, setUpdateLoading] = useState(false);
    const [editedCollection, setEditedCollection] = useState<Collection | null>(null);

    const { showMessage } = useSnackbar();

    const handleCollectionChange = (field: keyof Collection, value: any) => {
        setEditedCollection((prev) => prev ? ({
            ...prev,
            [field]: value,
        }) : null);
    };

    const preCollectionUpdate = () => {
        if (!editedCollection || !collection) return;

        if (editedCollection.status === CollectionStatus.PUBLISHED && !isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }
        
        
        updateCollectionDetails();
    };

    const updateCollectionDetails = async () => {
        if (!editedCollection) return;
        
        try {
            setUpdateLoading(true);
            await updateCollection(Number(classroom_id), editedCollection);
            setOpen(false);
        } catch (error) {
            console.error('Failed to update collection:', error);
        } finally {
            setUpdateLoading(false);
        }
    };

    useEffect(() => {
        if (collection) {
            setEditedCollection({ ...collection });
        }
    }, [collection]);

    if (!collection || !editedCollection) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxHeight: "90vh",
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
                    },
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="h6" fontWeight={600} sx={{ color: '#1e293b' }}>
                            Edit Collection
                        </Typography>
                        <IconButton onClick={() => setOpen(false)}>
                            <Close />
                        </IconButton>   
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={3}>
                        <TextField
                            label="Collection Title"
                            fullWidth
                            value={editedCollection.title}
                            onChange={(e) => handleCollectionChange("title", e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3b82f6',
                                        borderWidth: 2,
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#3b82f6',
                                },
                            }}
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b', fontWeight: 500 }}>
                                Description
                            </Typography>
                            <RichTextEditor
                                id={String(collection.id)}
                                type="description"
                                value={collection.description}
                                onChange={(val) => handleCollectionChange("description", val)}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            onClick={preCollectionUpdate}
                            disabled={updateLoading}
                            sx={{
                                backgroundColor: "#10b981",
                                height: 44,
                                fontWeight: 600,
                                borderRadius: 2,
                                alignSelf: 'flex-start',
                                px: 3,
                            }}
                        >
                            {updateLoading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                            {updateLoading ? "Saving..." : "Save Changes"}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

        </Box>
    );
}